import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './CryptoBlackjack.css';

// Contract ABIs
const CRYPTO_BLACKJACK_ABI = [
  "function playBlackjack(uint256 betAmount) external",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8[] playerCards, uint8[] houseCards, uint8 playerTotal, uint8 houseTotal, uint8 outcome, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won)[])",
  "function getGameConfig() external view returns (tuple(uint256 noWinChance, uint256 playerBlackjackChance, uint256 playerWinChance, uint256 pushChance, uint256 playerBustChance, uint256 blackjackPayout, uint256 regularWinPayout))",
  "function getOutcomeInfo(uint8 outcomeIndex) external view returns (string name, uint256 chance, uint256 payout)",
  "function calculateHouseEdge() external view returns (uint256)",
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function owner() external view returns (address)",
  "function admin() external view returns (address)", 
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function fundHouse(uint256 amount) external",
  "function withdrawHouse(uint256 amount) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function updateOutcomeChances(uint256 noWin, uint256 playerBlackjack, uint256 playerWin, uint256 push, uint256 playerBust) external",
  "function updatePayouts(uint256 blackjackPayout, uint256 regularPayout) external",
  "event GamePlayed(address indexed player, uint256 betAmount, uint8 outcome, uint8 playerTotal, uint8 houseTotal, uint256 grossPayout, uint256 netPayout, bool won)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  CRYPTO_BLACKJACK: "0xBb82643E7Ae9e0Daf459AA4fCa7FE0E17f7B3BEF", // Replace with actual address
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"
};

// Game outcomes configuration
const BLACKJACK_OUTCOMES = [
  { id: 0, name: 'PLAYER BLACKJACK', icon: '🃏', color: '#FFD700', description: 'Perfect 21 with 2 cards!', multiplier: '2.5x', sound: 'blackjack' },
  { id: 1, name: 'PLAYER WIN', icon: '🏆', color: '#32CD32', description: 'Closer to 21 than dealer', multiplier: '2x', sound: 'win' },
  { id: 2, name: 'PUSH', icon: '🤝', color: '#87CEEB', description: 'Tie game - bet returned', multiplier: '1x', sound: 'push' },
  { id: 3, name: 'PLAYER BUST', icon: '💥', color: '#FF4500', description: 'Over 21 - you lose', multiplier: '0x', sound: 'bust' },
  { id: 4, name: 'HOUSE WIN', icon: '🏠', color: '#DC143C', description: 'Dealer wins', multiplier: '0x', sound: 'loss' },
  { id: 5, name: 'NO WIN', icon: '❌', color: '#8B0000', description: 'House edge activated', multiplier: '0x', sound: 'loss' }
];

// Card suits and values
const CARD_SUITS = ['♠', '♥', '♦', '♣'];
const CARD_VALUES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

// 🎰 CASINO SOUND SYSTEM 🎰
const playCasinoSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 50);
      });
    };

    switch (type) {
      case 'cardShuffle':
        createComplexSound([
          { freq: 300, duration: 0.3, wave: 'sawtooth', delay: 0 },
          { freq: 400, duration: 0.25, wave: 'triangle', delay: 100 },
          { freq: 350, duration: 0.2, wave: 'sine', delay: 200 },
          { freq: 450, duration: 0.15, wave: 'triangle', delay: 300 }
        ], 0.2);
        break;
        
      case 'cardDeal':
        createComplexSound([
          { freq: 500, duration: 0.2, wave: 'triangle', delay: 0 },
          { freq: 400, duration: 0.15, wave: 'sine', delay: 50 }
        ], 0.15);
        break;
        
      case 'cardFlip':
        createComplexSound([
          { freq: 600, duration: 0.1, wave: 'square', delay: 0 },
          { freq: 800, duration: 0.08, wave: 'triangle', delay: 30 }
        ], 0.08);
        break;
        
      case 'blackjack':
        createComplexSound([
          { freq: 523, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 659, duration: 0.4, wave: 'triangle', delay: 200 },
          { freq: 784, duration: 0.5, wave: 'sine', delay: 400 },
          { freq: 1047, duration: 0.6, wave: 'sine', delay: 600 },
          { freq: 1319, duration: 0.7, wave: 'sine', delay: 800 }
        ], 0.5);
        break;
        
      case 'win':
        createComplexSound([
          { freq: 440, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 554, duration: 0.4, wave: 'sine', delay: 200 },
          { freq: 659, duration: 0.5, wave: 'triangle', delay: 400 }
        ], 0.4);
        break;
        
      case 'bust':
        createComplexSound([
          { freq: 220, duration: 0.8, wave: 'sawtooth', delay: 0 },
          { freq: 110, duration: 1.0, wave: 'triangle', delay: 300 },
          { freq: 55, duration: 1.2, wave: 'sine', delay: 600 }
        ], 0.6);
        break;
        
      case 'push':
        createComplexSound([
          { freq: 400, duration: 0.3, wave: 'sine', delay: 0 },
          { freq: 400, duration: 0.3, wave: 'sine', delay: 200 }
        ], 0.3);
        break;
        
      case 'chipClick':
        createComplexSound([
          { freq: 800, duration: 0.1, wave: 'square', delay: 0 },
          { freq: 1000, duration: 0.08, wave: 'triangle', delay: 30 }
        ], 0.08);
        break;
        
      case 'casinoAmbient':
        createTone(200, 0.15, 'sine');
        break;
        
      case 'neonBuzz':
        createTone(120, 0.2, 'sawtooth');
        break;
        
      default:
        createTone(440, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const CryptoBlackjack = ({ walletData, onBack }) => {
  const [casinoState, setCasinoState] = useState({
    betAmount: '10',
    isPlaying: false,
    isApproving: false,
    lastGame: null,
    balance: '0',
    allowance: '0',
    playerLevel: 1,
    casinoPoints: 0,
    totalGames: 0,
    gamesWon: 0,
    showGameModal: false,
    dealingCards: false,
    playerCards: [],
    houseCards: [],
    playerTotal: 0,
    houseTotal: 0,
    gameOutcome: null,
    showPlayerCards: false,
    showHouseCards: false
  });

  const [casinoStats, setCasinoStats] = useState({
    totalGames: 0,
    totalBets: '0',
    totalWinnings: '0',
    houseEdge: '15',
    bestMultiplier: '2.50',
    activePlayers: 0
  });

  const [gameHistory, setGameHistory] = useState([]);
  const [gamePhase, setGamePhase] = useState('idle');
  const [gameResult, setGameResult] = useState(null);
  const [neonEffects, setNeonEffects] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    cryptoBlackjack: null,
    blbToken: null
  });
  
  // Admin Panel State
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminState, setAdminState] = useState({
    houseBalance: '0',
    isPaused: false,
    fundAmount: '',
    withdrawAmount: '',
    minBet: '1',
    maxBet: '1000000',
    newOutcomeChances: {
      noWin: '5.0',
      playerBlackjack: '8.0',
      playerWin: '40.0',
      push: '7.0',
      playerBust: '15.0'
    },
    newPayouts: {
      blackjack: '250',
      regular: '200'
    }
  });

  const tableRef = useRef(null);
  const cardDeckRef = useRef(null);

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playCasinoSound(type, volume);
    }
  };

  // Neon casino effects
  useEffect(() => {
    const generateNeonEffects = () => {
      const effects = [];
      const effectCount = 30 + (casinoState.playerLevel * 2);
      
      const effectTypes = ['💎', '🎰', '🃏', '💰', '🎲', '⭐', '💫'];
      
      for (let i = 0; i < effectCount; i++) {
        effects.push({
          id: i,
          type: effectTypes[Math.floor(Math.random() * effectTypes.length)],
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 18 + 12,
          speed: Math.random() * 0.6 + 0.4,
          opacity: Math.random() * 0.5 + 0.2,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 1.5,
          glowIntensity: Math.random() * 0.8 + 0.2
        });
      }
      setNeonEffects(effects);
    };

    generateNeonEffects();
    
    const interval = setInterval(() => {
      setNeonEffects(prev => prev.map(effect => ({
        ...effect,
        y: (effect.y + effect.speed * 0.08) % 105,
        x: effect.x + Math.sin(Date.now() * 0.001 + effect.id) * 0.15,
        rotation: (effect.rotation + effect.rotationSpeed) % 360,
        opacity: 0.2 + Math.sin(Date.now() * 0.002 + effect.id) * effect.glowIntensity,
        glowIntensity: 0.2 + Math.sin(Date.now() * 0.003 + effect.id) * 0.6
      })));
    }, 120);

    return () => clearInterval(interval);
  }, [casinoState.playerLevel]);

  // Initialize contracts
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ cryptoBlackjack: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const cryptoBlackjackContract = new ethers.Contract(
        CONTRACT_ADDRESSES.CRYPTO_BLACKJACK,
        CRYPTO_BLACKJACK_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        cryptoBlackjack: cryptoBlackjackContract,
        blbToken: blbTokenContract
      });

      setTimeout(() => {
        loadCasinoData(cryptoBlackjackContract);
        loadBalance(blbTokenContract, cryptoBlackjackContract);
        checkAdminAccess(cryptoBlackjackContract);
      }, 500);

    } catch (error) {
      console.log('Error initializing contracts:', error);
      setContracts({ cryptoBlackjack: null, blbToken: null });
    }
  };

  // Check admin access
  const checkAdminAccess = async (contract = contracts.cryptoBlackjack) => {
    if (!contract || !walletData?.account) return;

    try {
      const owner = await contract.owner();
      const userIsOwner = owner.toLowerCase() === walletData.account.toLowerCase();
      
      const admin = await contract.admin();
      const userIsAdmin = admin.toLowerCase() === walletData.account.toLowerCase();

      setIsOwner(userIsOwner);
      setIsAdmin(userIsAdmin);

      if (userIsOwner || userIsAdmin) {
        await loadAdminData(contract);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsOwner(false);
      setIsAdmin(false);
    }
  };

  // Load admin data
  const loadAdminData = async (contract = contracts.cryptoBlackjack) => {
    if (!contract) return;

    try {
      const [houseBalance, isPaused, betLimits] = await Promise.all([
        contract.houseBalance(),
        contract.paused(),
        Promise.all([contract.minBet(), contract.maxBet()])
      ]);

      setAdminState(prev => ({
        ...prev,
        houseBalance: ethers.utils.formatEther(houseBalance),
        isPaused,
        minBet: ethers.utils.formatEther(betLimits[0]),
        maxBet: ethers.utils.formatEther(betLimits[1])
      }));
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Load casino data
  const loadCasinoData = async (contract = contracts.cryptoBlackjack) => {
    if (!contract) return;

    try {
      const [stats, recentGames, config] = await Promise.all([
        contract.getGameStats(),
        contract.getRecentGames(5).catch(() => []),
        contract.getGameConfig()
      ]);

      setCasinoStats({
        totalGames: parseInt(stats.totalGames.toString()),
        totalBets: ethers.utils.formatEther(stats.totalBets),
        totalWinnings: ethers.utils.formatEther(stats.totalPayoutsAmount),
        houseEdge: '15',
        bestMultiplier: '2.50',
        activePlayers: Math.floor(Math.random() * 200) + 50
      });

      if (recentGames && recentGames.length > 0) {
        const formattedHistory = recentGames.slice(0, 5).map(game => ({
          outcome: parseInt(game.outcome),
          playerTotal: parseInt(game.playerTotal),
          houseTotal: parseInt(game.houseTotal),
          won: game.won,
          betAmount: ethers.utils.formatEther(game.betAmount),
          winnings: ethers.utils.formatEther(game.netPayout),
          timestamp: parseInt(game.timestamp) * 1000
        }));
        setGameHistory(formattedHistory);
      }
    } catch (error) {
      console.error('Error loading casino data:', error);
    }
  };

  // Load balance
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.cryptoBlackjack) => {
    if (!tokenContract || !gameContract || !walletData?.account) return;

    try {
      const [balance, allowance] = await Promise.all([
        tokenContract.balanceOf(walletData.account),
        tokenContract.allowance(walletData.account, gameContract.address)
      ]);

      setCasinoState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  // Generate card display
  const generateCard = (value, isVisible = true) => {
    if (!isVisible) {
      return { display: '🂠', suit: '', value: '', color: '#000080' };
    }
    
    const suit = CARD_SUITS[Math.floor(Math.random() * 4)];
    const cardValue = value === 1 ? 'A' : (value > 10 ? ['J', 'Q', 'K'][value - 11] : value.toString());
    const color = (suit === '♥' || suit === '♦') ? '#DC143C' : '#000000';
    
    return {
      display: cardValue,
      suit: suit,
      value: cardValue,
      color: color
    };
  };

  // Handle blackjack game
  const handlePlayBlackjack = async () => {
    if (!contracts.cryptoBlackjack || !contracts.blbToken) {
      setErrorMessage('Casino connection lost. Please reconnect.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');

      const betAmount = parseFloat(casinoState.betAmount);
      const balance = parseFloat(casinoState.balance);

      if (betAmount <= 0) {
        setErrorMessage('Bet amount must be greater than 0');
        return;
      }

      if (betAmount > balance) {
        setErrorMessage('Insufficient chips');
        return;
      }

      // Start game sequence
      playGameSound('cardShuffle', 0.5);
      
      setCasinoState(prev => ({ 
        ...prev, 
        isPlaying: true,
        showGameModal: true,
        dealingCards: true,
        playerCards: [],
        houseCards: [],
        showPlayerCards: false,
        showHouseCards: false
      }));
      setGamePhase('dealing');

      // Card dealing sequence
      setTimeout(() => {
        playGameSound('cardDeal', 0.4);
        setCasinoState(prev => ({ ...prev, showPlayerCards: true }));
      }, 500);

      setTimeout(() => {
        playGameSound('cardDeal', 0.4);
        setCasinoState(prev => ({ ...prev, showHouseCards: true }));
      }, 1000);

      setTimeout(() => {
        executeBlackjackGame();
      }, 2000);

    } catch (error) {
      console.error('Blackjack game error:', error);
      setGamePhase('idle');
      setCasinoState(prev => ({ 
        ...prev, 
        isPlaying: false,
        showGameModal: false,
        dealingCards: false
      }));
    }
  };

  const executeBlackjackGame = async () => {
    try {
      playGameSound('cardFlip', 0.6);
      setGamePhase('playing');

      const betAmountWei = ethers.utils.parseEther(casinoState.betAmount);
      const tx = await contracts.cryptoBlackjack.playBlackjack(betAmountWei);
      
      setSuccessMessage('🃏 Cards dealt! Calculating result...');
      
      const receipt = await tx.wait();
      
      const gamePlayedEvent = receipt.events?.find(event => event.event === 'GamePlayed');
      
      if (gamePlayedEvent) {
        const [player, betAmount, outcome, playerTotal, houseTotal, grossPayout, netPayout, won] = gamePlayedEvent.args;
        
        const blackjackResult = {
          player,
          betAmount: ethers.utils.formatEther(betAmount),
          outcome: parseInt(outcome),
          playerTotal: parseInt(playerTotal),
          houseTotal: parseInt(houseTotal),
          grossPayout: ethers.utils.formatEther(grossPayout),
          netPayout: ethers.utils.formatEther(netPayout),
          won,
          timestamp: Date.now()
        };

        // Generate visual cards based on totals
        const playerCards = generateCardsForTotal(blackjackResult.playerTotal);
        const houseCards = generateCardsForTotal(blackjackResult.houseTotal);

        setCasinoState(prev => ({
          ...prev,
          playerCards,
          houseCards,
          playerTotal: blackjackResult.playerTotal,
          houseTotal: blackjackResult.houseTotal,
          gameOutcome: blackjackResult.outcome
        }));

        setTimeout(() => {
          handleGameResult(blackjackResult);
        }, 1500);
        
        await Promise.all([loadCasinoData(), loadBalance()]);
      } else {
        throw new Error('Game event not found');
      }

    } catch (error) {
      console.error('Blackjack execution error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Game cancelled by player');
      } else if (error.message.includes('insufficient funds')) {
        setErrorMessage('Insufficient chips or gas');
      } else if (error.message.includes('allowance')) {
        setErrorMessage('Please approve chip spending first');
      } else {
        setErrorMessage(`Game failed: ${error.reason || error.message}`);
      }
      
      setGamePhase('idle');
      setCasinoState(prev => ({ 
        ...prev, 
        isPlaying: false,
        showGameModal: false
      }));
      setSuccessMessage('');
    }
  };

  const generateCardsForTotal = (total) => {
    // Generate realistic-looking cards that add up to the total
    const cards = [];
    let remaining = total;
    
    if (total === 21 && Math.random() < 0.5) {
      // Blackjack possibility
      cards.push(generateCard(1)); // Ace
      cards.push(generateCard(10)); // 10-value card
    } else if (total <= 21) {
      // Generate 2-3 cards that sum to total
      const numCards = Math.min(3, Math.max(2, Math.floor(Math.random() * 2) + 2));
      
      for (let i = 0; i < numCards - 1; i++) {
        const maxCard = Math.min(10, remaining - (numCards - i - 1));
        const minCard = Math.max(1, remaining - (numCards - i - 1) * 10);
        const cardValue = Math.floor(Math.random() * (maxCard - minCard + 1)) + minCard;
        
        cards.push(generateCard(cardValue));
        remaining -= cardValue;
      }
      
      if (remaining > 0 && remaining <= 10) {
        cards.push(generateCard(remaining));
      }
    } else {
      // Bust - generate cards that go over 21
      cards.push(generateCard(10));
      cards.push(generateCard(Math.max(8, total - 10)));
    }
    
    return cards;
  };

  const handleGameResult = (blackjackResult) => {
    setGameResult(blackjackResult);
    setGamePhase('result');

    setTimeout(() => {
      const outcome = BLACKJACK_OUTCOMES[blackjackResult.outcome];
      
      if (blackjackResult.won) {
        if (blackjackResult.outcome === 0) { // Blackjack
          playGameSound('blackjack', 0.9);
          setSuccessMessage(`🃏 BLACKJACK! Perfect 21! +${formatNumber(blackjackResult.netPayout)} BLB`);
          setCasinoState(prev => ({
            ...prev,
            casinoPoints: prev.casinoPoints + 2000
          }));
        } else if (blackjackResult.outcome === 2) { // Push
          playGameSound('push', 0.5);
          setSuccessMessage(`🤝 PUSH! It's a tie! Bet returned: ${formatNumber(blackjackResult.netPayout)} BLB`);
          setCasinoState(prev => ({
            ...prev,
            casinoPoints: prev.casinoPoints + 100
          }));
        } else { // Regular win
          playGameSound('win', 0.7);
          setSuccessMessage(`🏆 YOU WIN! Beat the house! +${formatNumber(blackjackResult.netPayout)} BLB`);
          setCasinoState(prev => ({
            ...prev,
            casinoPoints: prev.casinoPoints + 1000,
            gamesWon: prev.gamesWon + 1
          }));
        }
      } else {
        if (blackjackResult.outcome === 3) { // Bust
          playGameSound('bust', 0.6);
          setErrorMessage(`💥 BUST! Over 21! You lose -${formatNumber(blackjackResult.betAmount)} BLB`);
        } else { // House win
          playGameSound('bust', 0.4);
          setErrorMessage(`🏠 HOUSE WINS! Better luck next time! -${formatNumber(blackjackResult.betAmount)} BLB`);
        }
        setCasinoState(prev => ({
          ...prev,
          casinoPoints: prev.casinoPoints + 50
        }));
      }
    }, 500);

    setCasinoState(prev => ({
      ...prev,
      isPlaying: false,
      totalGames: prev.totalGames + 1,
      playerLevel: Math.floor((prev.casinoPoints + (blackjackResult.won ? 1000 : 50)) / 2500) + 1
    }));

    setTimeout(() => {
      setGamePhase('idle');
      setGameResult(null);
      setCasinoState(prev => ({ 
        ...prev, 
        isPlaying: false,
        showGameModal: false,
        dealingCards: false
      }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
  };

  // Handle token approval
  const handleApprove = async () => {
    if (!contracts.blbToken || !contracts.cryptoBlackjack) {
      setErrorMessage('Casino connection lost. Please reconnect.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');
      playGameSound('chipClick', 0.3);
      setCasinoState(prev => ({ ...prev, isApproving: true }));

      const maxAmount = ethers.constants.MaxUint256;
      const tx = await contracts.blbToken.approve(contracts.cryptoBlackjack.address, maxAmount);
      
      setSuccessMessage('Casino approval processing...');
      await tx.wait();
      
      await loadBalance();
      setSuccessMessage('✅ Casino access approved! Ready to play!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Casino access denied');
      } else {
        setErrorMessage(`Approval failed: ${error.reason || error.message}`);
      }
    } finally {
      setCasinoState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const needsApproval = () => {
    return parseFloat(casinoState.allowance) < parseFloat(casinoState.betAmount);
  };

  const canPlayBlackjack = () => {
    return connected && 
           contracts.cryptoBlackjack &&
           contracts.blbToken &&
           parseFloat(casinoState.balance) >= parseFloat(casinoState.betAmount) &&
           parseFloat(casinoState.betAmount) > 0 &&
           !needsApproval() &&
           !casinoState.isPlaying;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(2);
  };

  const getPlayerTitle = (level) => {
    if (level >= 50) return 'High Roller Legend';
    if (level >= 40) return 'Casino Master';
    if (level >= 30) return 'Blackjack Pro';
    if (level >= 20) return 'Card Sharp';
    if (level >= 10) return 'Casino Regular';
    return 'Casino Newbie';
  };

  const getOutcomeName = (outcome) => {
    return BLACKJACK_OUTCOMES[outcome]?.name || 'UNKNOWN';
  };

  // Connection state
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  if (!connected) {
    return (
      <div className="vegas-casino-floor">
        <div className="neon-effects">
          {neonEffects.map(effect => (
            <div
              key={effect.id}
              className="neon-particle"
              style={{
                left: `${effect.x}%`,
                top: `${effect.y}%`,
                opacity: effect.opacity,
                fontSize: `${effect.size}px`,
                transform: `rotate(${effect.rotation}deg)`,
                filter: `drop-shadow(0 0 ${effect.glowIntensity * 15}px currentColor)`,
                animationDelay: `${effect.id * 0.2}s`
              }}
            >
              {effect.type}
            </div>
          ))}
        </div>
        <div className="casino-access-denied">
          <div className="access-icon">🎰</div>
          <h3 className="access-title">🚫 CASINO CLOSED</h3>
          <p className="access-desc">Present your membership card to enter the VIP Casino Floor</p>
        </div>
      </div>
    );
  }

  if (!contracts.cryptoBlackjack || !contracts.blbToken) {
    return (
      <div className="vegas-casino-floor">
        <div className="neon-effects">
          {neonEffects.map(effect => (
            <div
              key={effect.id}
              className="neon-particle"
              style={{
                left: `${effect.x}%`,
                top: `${effect.y}%`,
                opacity: effect.opacity,
                fontSize: `${effect.size}px`,
                transform: `rotate(${effect.rotation}deg)`,
                filter: `drop-shadow(0 0 ${effect.glowIntensity * 15}px currentColor)`,
                animationDelay: `${effect.id * 0.2}s`
              }}
            >
              {effect.type}
            </div>
          ))}
        </div>
        <div className="casino-access-denied">
          <div className="access-icon">🎲</div>
          <h3 className="access-title">🔌 CONNECTING TO CASINO</h3>
          <p className="access-desc">Establishing secure connection to casino servers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vegas-casino-floor">
      {/* Neon Effects Background */}
      <div className="neon-effects">
        {neonEffects.map(effect => (
          <div
            key={effect.id}
            className="neon-particle"
            style={{
              left: `${effect.x}%`,
              top: `${effect.y}%`,
              opacity: effect.opacity,
              fontSize: `${effect.size}px`,
              transform: `rotate(${effect.rotation}deg)`,
              filter: `drop-shadow(0 0 ${effect.glowIntensity * 15}px currentColor)`,
              animationDelay: `${effect.id * 0.2}s`
            }}
          >
            {effect.type}
          </div>
        ))}
      </div>

      {/* Casino Header */}
      <div className="casino-header-console">
        <button 
          className="casino-exit-btn" 
          onClick={() => {
            playGameSound('chipClick', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('casinoAmbient', 0.2)}
        >
          ← EXIT CASINO
        </button>
        <div className="casino-title-section">
          <h2 className="casino-main-title">🎰 CRYPTO BLACKJACK CASINO</h2>
          <div className="casino-subtitle">High Stakes Digital Gaming</div>
        </div>
        <div className="chip-stack-display">
          🎲 {formatNumber(casinoState.balance)} BLB
          <div className="player-level-display">
            {getPlayerTitle(casinoState.playerLevel)}
          </div>
          <button
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('chipClick', 0.2);
            }}
            className="sound-toggle-btn"
            title={soundEnabled ? 'Mute Sounds' : 'Enable Sounds'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          {(isOwner || isAdmin) && (
            <button
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('chipClick', 0.3);
              }}
              onMouseEnter={() => playGameSound('casinoAmbient', 0.2)}
              className={`casino-master-btn ${showAdminPanel ? 'active' : ''}`}
              title="Casino Management Panel"
            >
              ⚙️
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="casino-message casino-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="casino-message casino-success-msg">
          {successMessage}
        </div>
      )}

      <div className="casino-layout">
        {/* Admin Panel */}
        {showAdminPanel && (isOwner || isAdmin) && (
          <div className="casino-master-panel">
            <div className="casino-master-panel-header">
              <h3>🎰 CASINO MANAGEMENT CENTER</h3>
              <div className="casino-master-role-badge">
                {isOwner ? '👑 CASINO OWNER' : '🎰 PIT BOSS'}
              </div>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="casino-master-close-btn"
                style={{
                  background: 'rgba(255, 69, 0, 0.3)',
                  border: '2px solid #FF4500',
                  borderRadius: '8px',
                  color: '#FFB347',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem'
                }}
              >
                ✕
              </button>
            </div>

            <div className="casino-master-panel-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '2rem' }}>
              {/* Casino Operations Status */}
              <div className="casino-master-section" style={{
                background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.2) 0%, rgba(255, 215, 0, 0.1) 100%)',
                border: '3px solid rgba(220, 20, 60, 0.5)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="casino-master-section-title" style={{ color: '#DC143C', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🎰 Casino Operations Status
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{ color: 'rgba(220, 20, 60, 0.8)', fontWeight: '600' }}>Status:</span>
                  <span style={{
                    color: adminState.isPaused ? '#FF4500' : '#FFD700',
                    fontWeight: '800',
                    textShadow: '0 0 10px currentColor'
                  }}>
                    {adminState.isPaused ? '⏸️ CLOSED' : '▶️ OPEN'}
                  </span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('chipClick', 0.3);
                      
                      if (contracts.cryptoBlackjack) {
                        const tx = adminState.isPaused ? 
                          await contracts.cryptoBlackjack.unpauseGame() : 
                          await contracts.cryptoBlackjack.pauseGame();
                        
                        setSuccessMessage(`Casino ${adminState.isPaused ? 'opening' : 'closing'}...`);
                        await tx.wait();
                        
                        await loadAdminData();
                        setSuccessMessage(`✅ Casino ${adminState.isPaused ? 'opened' : 'closed'} successfully!`);
                      } else {
                        setSuccessMessage(`✅ Casino ${adminState.isPaused ? 'opened' : 'closed'}! (Demo)`);
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Error toggling casino:', error);
                      setErrorMessage(`Failed to ${adminState.isPaused ? 'open' : 'close'} casino: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: adminState.isPaused ? 
                      'linear-gradient(135deg, #FFD700 0%, #32CD32 100%)' : 
                      'linear-gradient(135deg, #FF4500 0%, #DC143C 100%)',
                    border: `3px solid ${adminState.isPaused ? '#FFD700' : '#FF4500'}`,
                    borderRadius: '10px',
                    color: '#FFF',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: `0 0 15px rgba(${adminState.isPaused ? '255, 215, 0' : '255, 69, 0'}, 0.4)`
                  }}
                >
                  {adminState.isPaused ? '▶️ OPEN CASINO' : '⏸️ CLOSE CASINO'}
                </button>
              </div>

              {/* House Vault */}
              <div className="casino-master-section" style={{
                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%)',
                border: '3px solid rgba(255, 215, 0, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="casino-master-section-title" style={{ color: '#FFD700', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  💰 House Vault
                </h4>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ color: 'rgba(255, 215, 0, 0.8)', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Current Vault:
                  </div>
                  <div style={{ 
                    color: '#FFD700', 
                    fontSize: '1.5rem', 
                    fontWeight: '800',
                    textShadow: '0 0 15px currentColor'
                  }}>
                    🎰 {formatNumber(adminState.houseBalance)} BLB
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('chipClick', 0.3);
                      
                      if (contracts.cryptoBlackjack && contracts.cryptoBlackjack.emergencyWithdrawAll) {
                        const tx = await contracts.cryptoBlackjack.emergencyWithdrawAll();
                        setSuccessMessage('Emptying house vault...');
                        await tx.wait();
                        
                        await Promise.all([loadAdminData(), loadBalance()]);
                        setSuccessMessage('✅ Emergency vault withdrawal completed!');
                      } else {
                        setSuccessMessage('✅ Emergency vault withdrawal completed! (Demo)');
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Emergency withdraw error:', error);
                      setErrorMessage(`Emergency withdrawal failed: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: 'linear-gradient(135deg, #FF4500 0%, #DC143C 100%)',
                    border: '3px solid #FF4500',
                    borderRadius: '10px',
                    color: '#FFF',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: '0 0 15px rgba(255, 69, 0, 0.4)'
                  }}
                >
                  🚨 EMERGENCY WITHDRAWAL
                </button>
              </div>

              {/* Fund Vault */}
              <div className="casino-master-section" style={{
                background: 'linear-gradient(135deg, rgba(50, 205, 50, 0.15) 0%, rgba(34, 139, 34, 0.1) 100%)',
                border: '3px solid rgba(50, 205, 50, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="casino-master-section-title" style={{ color: '#32CD32', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  💎 Fund Vault
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.fundAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, fundAmount: e.target.value }))}
                    placeholder="Amount to fund house vault"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '2px solid rgba(50, 205, 50, 0.5)',
                      borderRadius: '10px',
                      color: '#32CD32',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.fundAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid fund amount');
                          return;
                        }
                        
                        playGameSound('chipClick', 0.3);
                        
                        if (contracts.cryptoBlackjack && contracts.blbToken) {
                          const amountWei = ethers.utils.parseEther(amount);
                          
                          const allowance = await contracts.blbToken.allowance(walletData.account, contracts.cryptoBlackjack.address);
                          if (allowance.lt(amountWei)) {
                            const approveTx = await contracts.blbToken.approve(contracts.cryptoBlackjack.address, ethers.constants.MaxUint256);
                            await approveTx.wait();
                          }
                          
                          const tx = await contracts.cryptoBlackjack.fundHouse(amountWei);
                          setSuccessMessage('Funding vault...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Vault funded with ${formatNumber(amount)} BLB!`);
                        } else {
                          setSuccessMessage(`✅ Vault funded with ${formatNumber(amount)} BLB! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, fundAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error funding vault:', error);
                        setErrorMessage(`Failed to fund vault: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.fundAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #32CD32 0%, #228B22 100%)',
                      border: '3px solid #32CD32',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.fundAmount ? 1 : 0.6,
                      boxShadow: adminState.fundAmount ? '0 0 15px rgba(50, 205, 50, 0.4)' : 'none'
                    }}
                  >
                    💎 FUND VAULT
                  </button>
                </div>
              </div>

              {/* Withdraw Vault */}
              <div className="casino-master-section" style={{
                background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.15) 0%, rgba(255, 69, 0, 0.1) 100%)',
                border: '3px solid rgba(255, 165, 0, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="casino-master-section-title" style={{ color: '#FFA500', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  💸 Withdraw Vault
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.withdrawAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, withdrawAmount: e.target.value }))}
                    placeholder="Amount to withdraw"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '2px solid rgba(255, 165, 0, 0.5)',
                      borderRadius: '10px',
                      color: '#FFA500',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.withdrawAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid withdraw amount');
                          return;
                        }
                        
                        playGameSound('chipClick', 0.3);
                        
                        if (contracts.cryptoBlackjack) {
                          const amountWei = ethers.utils.parseEther(amount);
                          const tx = await contracts.cryptoBlackjack.withdrawHouse(amountWei);
                          setSuccessMessage('Withdrawing from vault...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from vault!`);
                        } else {
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from vault! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, withdrawAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error withdrawing from vault:', error);
                        setErrorMessage(`Failed to withdraw: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.withdrawAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #FFA500 0%, #FF4500 100%)',
                      border: '3px solid #FFA500',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.withdrawAmount ? 1 : 0.6,
                      boxShadow: adminState.withdrawAmount ? '0 0 15px rgba(255, 165, 0, 0.4)' : 'none'
                    }}
                  >
                    💸 WITHDRAW
                  </button>
                </div>
              </div>

              {/* Betting Limits */}
              <div className="casino-master-section" style={{
                background: 'linear-gradient(135deg, rgba(138, 43, 226, 0.15) 0%, rgba(75, 0, 130, 0.1) 100%)',
                border: '3px solid rgba(138, 43, 226, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="casino-master-section-title" style={{ color: '#8A2BE2', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🎲 Betting Limits
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(138, 43, 226, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MIN BET
                      </label>
                      <input
                        type="number"
                        value={adminState.newMinBet || adminState.minBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMinBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(138, 43, 226, 0.5)',
                          borderRadius: '8px',
                          color: '#8A2BE2',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(138, 43, 226, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MAX BET
                      </label>
                      <input
                        type="number"
                        value={adminState.newMaxBet || adminState.maxBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMaxBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(138, 43, 226, 0.5)',
                          borderRadius: '8px',
                          color: '#8A2BE2',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const minBet = parseFloat(adminState.newMinBet || adminState.minBet);
                        const maxBet = parseFloat(adminState.newMaxBet || adminState.maxBet);

                        if (minBet <= 0 || maxBet <= 0 || minBet >= maxBet) {
                          setErrorMessage('Invalid betting limits');
                          return;
                        }

                        playGameSound('chipClick', 0.3);
                        
                        if (contracts.cryptoBlackjack) {
                          const minBetWei = ethers.utils.parseEther(minBet.toString());
                          const maxBetWei = ethers.utils.parseEther(maxBet.toString());
                          
                          const tx = await contracts.cryptoBlackjack.setBetLimits(minBetWei, maxBetWei);
                          setSuccessMessage('Updating betting limits...');
                          await tx.wait();
                          
                          await loadAdminData();
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB`);
                        } else {
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB (Demo)`);
                        }
                        
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error setting betting limits:', error);
                        setErrorMessage(`Failed to set limits: ${error.reason || error.message}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #8A2BE2 0%, #4B0082 100%)',
                      border: '3px solid #8A2BE2',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      boxShadow: '0 0 15px rgba(138, 43, 226, 0.4)'
                    }}
                  >
                    🎯 SET LIMITS
                  </button>
                </div>
              </div>

              {/* Game Configuration */}
              <div className="casino-master-section" style={{
                background: 'linear-gradient(135deg, rgba(25, 25, 112, 0.2) 0%, rgba(72, 61, 139, 0.1) 100%)',
                border: '3px solid rgba(25, 25, 112, 0.5)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="casino-master-section-title" style={{ color: '#e4e4e4ff', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🃏 Game Configuration
                </h4>
                <div style={{ fontSize: '0.85rem', color: 'rgba(253, 253, 253, 0.7)', marginBottom: '1rem', fontStyle: 'italic', textAlign: 'center' }}>
                  Configure blackjack outcome probabilities and payout rates
                </div>
                
                {/* Outcome Chances Configuration */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ color: '#f7f7f7ff', fontWeight: '700', marginBottom: '0.8rem', textAlign: 'center' }}>
                    OUTCOME PROBABILITIES (%)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        ❌ NO WIN CHANCE (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newOutcomeChances?.noWin || '5.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newOutcomeChances: { 
                            ...prev.newOutcomeChances, 
                            noWin: e.target.value 
                          }
                        }))}
                        min="0"
                        max="50"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#ffffffff',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        🃏 PLAYER BLACKJACK CHANCE (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newOutcomeChances?.playerBlackjack || '8.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newOutcomeChances: { 
                            ...prev.newOutcomeChances, 
                            playerBlackjack: e.target.value 
                          }
                        }))}
                        min="0"
                        max="30"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#ffffffff',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(241, 241, 241, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        🏆 PLAYER WIN CHANCE (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newOutcomeChances?.playerWin || '40.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newOutcomeChances: { 
                            ...prev.newOutcomeChances, 
                            playerWin: e.target.value 
                          }
                        }))}
                        min="0"
                        max="80"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#fdfdfdff',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(243, 243, 243, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        🤝 PUSH CHANCE (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newOutcomeChances?.push || '7.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newOutcomeChances: { 
                            ...prev.newOutcomeChances, 
                            push: e.target.value 
                          }
                        }))}
                        min="0"
                        max="30"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#ffffffff',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        💥 PLAYER BUST CHANCE (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newOutcomeChances?.playerBust || '15.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newOutcomeChances: { 
                            ...prev.newOutcomeChances, 
                            playerBust: e.target.value 
                          }
                        }))}
                        min="0"
                        max="50"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#f7f7f7ff',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Payout Configuration */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ color: '#ffffffff', fontWeight: '700', marginBottom: '0.8rem', textAlign: 'center' }}>
                    PAYOUT MULTIPLIERS
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        🃏 BLACKJACK PAYOUT (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newPayouts?.blackjack || '250'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newPayouts: { 
                            ...prev.newPayouts, 
                            blackjack: e.target.value 
                          }
                        }))}
                        min="150"
                        max="400"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#ffffffff',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(251, 251, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        🏆 REGULAR WIN PAYOUT (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newPayouts?.regular || '200'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newPayouts: { 
                            ...prev.newPayouts, 
                            regular: e.target.value 
                          }
                        }))}
                        min="150"
                        max="300"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#ffffffff',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Update Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const { noWin, playerBlackjack, playerWin, push, playerBust } = adminState.newOutcomeChances;
                        
                        const total = parseFloat(noWin || 0) + parseFloat(playerBlackjack || 0) + 
                                     parseFloat(playerWin || 0) + parseFloat(push || 0) + 
                                     parseFloat(playerBust || 0);

                        if (Math.abs(total - 75) > 1) { // Allow some tolerance, house win is remainder
                          setErrorMessage(`Total chances should sum to approximately 75% (House Win fills remainder). Current: ${total.toFixed(1)}%`);
                          return;
                        }

                        playGameSound('chipClick', 0.3);
                        
                        if (contracts.cryptoBlackjack) {
                          const noWinBP = Math.floor(parseFloat(noWin || 0) * 100);
                          const blackjackBP = Math.floor(parseFloat(playerBlackjack || 0) * 100);
                          const winBP = Math.floor(parseFloat(playerWin || 0) * 100);
                          const pushBP = Math.floor(parseFloat(push || 0) * 100);
                          const bustBP = Math.floor(parseFloat(playerBust || 0) * 100);
                          
                          const tx = await contracts.cryptoBlackjack.updateOutcomeChances(noWinBP, blackjackBP, winBP, pushBP, bustBP);
                          setSuccessMessage('Updating game probabilities...');
                          await tx.wait();
                          
                          await loadAdminData();
                          setSuccessMessage(`✅ Outcome chances updated successfully!`);
                        } else {
                          setSuccessMessage(`✅ Outcome chances updated successfully! (Demo)`);
                        }
                        
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error updating outcome chances:', error);
                        setErrorMessage(`Failed to update chances: ${error.reason || error.message}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #191970 0%, #4169E1 100%)',
                      border: '3px solid #191970',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      boxShadow: '0 0 15px rgba(25, 25, 112, 0.4)'
                    }}
                  >
                    🎯 UPDATE CHANCES
                  </button>
                  
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const blackjackPayout = parseFloat(adminState.newPayouts?.blackjack || '250');
                        const regularPayout = parseFloat(adminState.newPayouts?.regular || '200');

                        if (blackjackPayout < 150 || regularPayout < 150) {
                          setErrorMessage('Payouts must be at least 150%');
                          return;
                        }

                        playGameSound('chipClick', 0.3);
                        
                        if (contracts.cryptoBlackjack) {
                          const tx = await contracts.cryptoBlackjack.updatePayouts(blackjackPayout, regularPayout);
                          setSuccessMessage('Updating payout rates...');
                          await tx.wait();
                          
                          await loadAdminData();
                          setSuccessMessage(`✅ Payouts updated! Blackjack: ${blackjackPayout}%, Regular: ${regularPayout}%`);
                        } else {
                          setSuccessMessage(`✅ Payouts updated! Blackjack: ${blackjackPayout}%, Regular: ${regularPayout}% (Demo)`);
                        }
                        
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error updating payouts:', error);
                        setErrorMessage(`Failed to update payouts: ${error.reason || error.message}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #8A2BE2 0%, #4B0082 100%)',
                      border: '3px solid #8A2BE2',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      boxShadow: '0 0 15px rgba(138, 43, 226, 0.4)'
                    }}
                  >
                    🃏 UPDATE PAYOUTS
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Casino Area */}
        <div className="casino-gaming-area">
          {/* Game Modal */}
          {casinoState.showGameModal && (
            <div className="game-modal-overlay">
              <div className="game-modal">
                <div className="game-header">
                  <h2>🃏 BLACKJACK TABLE IN SESSION!</h2>
                  <div className="game-info">
                    <span>Bet: {formatNumber(casinoState.betAmount)} BLB</span>
                    <span>Player: {getPlayerTitle(casinoState.playerLevel)}</span>
                  </div>
                </div>

                <div className="blackjack-table">
                  {/* House Cards */}
                  <div className="house-area">
                    <div className="area-label">🏠 HOUSE</div>
                    <div className="card-hand">
                      {casinoState.houseCards.map((card, index) => (
                        <div 
                          key={index} 
                          className={`playing-card ${casinoState.showHouseCards ? 'revealed' : ''}`}
                          style={{ 
                            animationDelay: `${index * 0.3}s`,
                            color: card.color 
                          }}
                        >
                          <div className="card-value">{card.display}</div>
                          <div className="card-suit">{card.suit}</div>
                        </div>
                      ))}
                    </div>
                    {casinoState.houseTotal > 0 && (
                      <div className="hand-total">Total: {casinoState.houseTotal}</div>
                    )}
                  </div>

                  {/* Player Cards */}
                  <div className="player-area">
                    <div className="area-label">🎯 PLAYER</div>
                    <div className="card-hand">
                      {casinoState.playerCards.map((card, index) => (
                        <div 
                          key={index} 
                          className={`playing-card ${casinoState.showPlayerCards ? 'revealed' : ''}`}
                          style={{ 
                            animationDelay: `${index * 0.3}s`,
                            color: card.color 
                          }}
                        >
                          <div className="card-value">{card.display}</div>
                          <div className="card-suit">{card.suit}</div>
                        </div>
                      ))}
                    </div>
                    {casinoState.playerTotal > 0 && (
                      <div className="hand-total">Total: {casinoState.playerTotal}</div>
                    )}
                  </div>
                </div>

                {/* Result Display in Modal */}
                {gamePhase === 'result' && gameResult && (
                  <div className="game-result-display">
                    <div className={`game-result-content ${gameResult.won ? 'player-victory' : 'house-victory'}`}>
                      <div className="game-result-icon">
                        {BLACKJACK_OUTCOMES[gameResult.outcome]?.icon || '🎰'}
                      </div>
                      <h3 className="game-result-title">
                        {BLACKJACK_OUTCOMES[gameResult.outcome]?.name || 'GAME OVER'}
                      </h3>
                      <div className="game-result-amount">
                        {gameResult.won ? `+${formatNumber(gameResult.netPayout)}` : `-${formatNumber(gameResult.betAmount)}`} BLB
                      </div>
                      <div className="game-result-desc">
                        Player: {gameResult.playerTotal} | House: {gameResult.houseTotal}
                      </div>
                      {gameResult.outcome === 0 && gameResult.won && (
                        <div className="blackjack-celebration">
                          🃏 BLACKJACK! PERFECT 21! THE HOUSE BOWS! 🃏
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Dealing indicator */}
                {gamePhase === 'dealing' && (
                  <div className="dealing-indicator">
                    <div className="dealing-text">🃏 DEALING CARDS...</div>
                    <div className="dealing-subtext">Shuffling deck, dealing hands...</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Blackjack Table */}
          <div className="blackjack-table-area">
            <div className="table-display">
              <div className="table-header">
                <span>BLACKJACK TABLE - MINIMUM BET: 1 BLB</span>
                <span className="table-status">READY FOR ACTION</span>
              </div>
              
              <div className="felt-table" ref={tableRef}>
                {/* Card deck */}
                <div className="card-deck" ref={cardDeckRef}>
                  <div className="deck-cards">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="deck-card" style={{ zIndex: 8 - i }}>🂠</div>
                    ))}
                  </div>
                  <div className="deck-label">DECK</div>
                </div>
                
                {/* Betting circle */}
                <div className="betting-circle">
                  <div className="circle-inner">
                    <div className="bet-amount-display">
                      {formatNumber(casinoState.betAmount)} BLB
                    </div>
                    <div className="bet-label">YOUR BET</div>
                  </div>
                </div>
                
                {/* Game rules display */}
                <div className="rules-display">
                  <div className="rule-item">
                    <span className="rule-label">BLACKJACK:</span>
                    <span className="rule-value">2.5x</span>
                  </div>
                  <div className="rule-item">
                    <span className="rule-label">WIN:</span>
                    <span className="rule-value">2x</span>
                  </div>
                  <div className="rule-item">
                    <span className="rule-label">PUSH:</span>
                    <span className="rule-value">1x</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Game Controls */}
          <div className="casino-controls">
            {/* Bet Amount */}
            <div className="casino-control-panel">
              <label className="control-panel-label">🎲 BET AMOUNT</label>
              <div>
                <input
                  className="bet-amount-input"
                  type="number"
                  value={casinoState.betAmount}
                  onChange={(e) => setCasinoState(prev => ({ ...prev, betAmount: e.target.value }))}
                  disabled={casinoState.isPlaying}
                  min="1"
                  max={casinoState.balance}
                  step="1"
                  onFocus={() => playGameSound('chipClick', 0.1)}
                />
                <div className="bet-preset-buttons">
                  {['10', '50', '200'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        playGameSound('chipClick', 0.2);
                        setCasinoState(prev => ({ ...prev, betAmount: amount }));
                      }}
                      onMouseEnter={() => playGameSound('casinoAmbient', 0.1)}
                      disabled={casinoState.isPlaying}
                      className="bet-preset-btn"
                    >
                      {amount}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      playGameSound('chipClick', 0.2);
                      setCasinoState(prev => ({ ...prev, betAmount: Math.floor(parseFloat(prev.balance)).toString() }));
                    }}
                    onMouseEnter={() => playGameSound('casinoAmbient', 0.1)}
                    disabled={casinoState.isPlaying}
                    className="bet-preset-btn all-in-btn"
                  >
                    ALL IN
                  </button>
                </div>
              </div>
            </div>

            {/* Game Rules */}
            <div className="casino-control-panel">
              <label className="control-panel-label">🃏 GAME OUTCOMES</label>
              <div className="outcome-grid">
                {BLACKJACK_OUTCOMES.slice(0, 3).map((outcome) => (
                  <div key={outcome.id} className="outcome-card" style={{ borderColor: outcome.color }}>
                    <div className="outcome-icon" style={{ color: outcome.color }}>{outcome.icon}</div>
                    <div className="outcome-details">
                      <div className="outcome-name">{outcome.name}</div>
                      <div className="outcome-multiplier">{outcome.multiplier}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Button */}
            <div className="casino-control-panel">
              {needsApproval() ? (
                <button
                  className="casino-action-btn casino-approve-btn"
                  onClick={handleApprove}
                  onMouseEnter={() => playGameSound('casinoAmbient', 0.2)}
                  disabled={casinoState.isApproving}
                >
                  {casinoState.isApproving ? '🎰 JOINING...' : '🎲 JOIN CASINO'}
                </button>
              ) : (
                <button
                  className="casino-action-btn casino-play-btn"
                  onClick={handlePlayBlackjack}
                  onMouseEnter={() => playGameSound('casinoAmbient', 0.2)}
                  disabled={!canPlayBlackjack()}
                >
                  {casinoState.isPlaying ? '🃏 DEALING...' : '🃏 DEAL CARDS'}
                </button>
              )}
            </div>

            {/* Potential Winnings */}
            <div className="potential-winnings-display">
              💰 POTENTIAL WINNINGS: Up to 2.5x your bet
            </div>
          </div>
        </div>

        {/* Casino Leaderboard */}
        <div className="casino-leaderboard">
          <div className="leaderboard-panel">
            <h3 className="leaderboard-title">🎰 YOUR PROFILE</h3>
            <div className="leaderboard-entry">
              <span className="entry-label">Level</span>
              <span className="entry-value">{casinoState.playerLevel}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Points</span>
              <span className="entry-value">{formatNumber(casinoState.casinoPoints)}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Title</span>
              <span className="entry-value">{getPlayerTitle(casinoState.playerLevel)}</span>
            </div>
          </div>

          <div className="leaderboard-panel">
            <h3 className="leaderboard-title">🏠 CASINO STATS</h3>
            <div className="leaderboard-entry">
              <span className="entry-label">Players</span>
              <span className="entry-value">{casinoStats.activePlayers}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Games</span>
              <span className="entry-value">{formatNumber(casinoStats.totalGames)}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Payouts</span>
              <span className="entry-value">{formatNumber(casinoStats.totalWinnings)}</span>
            </div>
          </div>

          <div className="leaderboard-panel">
            <h3 className="leaderboard-title">🃏 HOW TO PLAY</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[
                'Place your bet',
                'Get dealt cards',
                'Beat the house',
                'Win up to 2.5x!'
              ].map((rule, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    background: 'linear-gradient(45deg, #DC143C, #FFD700)', 
                    color: '#FFF', 
                    width: '2rem', 
                    height: '2rem', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontWeight: '800', 
                    fontSize: '0.9rem', 
                    flexShrink: 0,
                    boxShadow: '0 0 8px rgba(220, 20, 60, 0.4)'
                  }}>{index + 1}</div>
                  <div style={{ color: 'rgba(255, 215, 0, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                    {rule}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {gameHistory.length > 0 && (
            <div className="leaderboard-panel">
              <h3 className="leaderboard-title">🎲 RECENT GAMES</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '200px', overflowY: 'auto' }}>
                {gameHistory.map((game, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.8rem',
                    background: 'rgba(220, 20, 60, 0.1)',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${game.won ? '#FFD700' : '#DC143C'}`,
                    border: '1px solid rgba(220, 20, 60, 0.3)'
                  }}>
                    <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                      {BLACKJACK_OUTCOMES[game.outcome]?.icon || '🎰'}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ color: 'rgba(255, 215, 0, 0.8)', fontSize: '0.8rem', fontWeight: '600' }}>
                          {BLACKJACK_OUTCOMES[game.outcome]?.name || 'GAME'}
                        </div>
                        <div style={{ 
                          color: game.won ? '#FFD700' : '#DC143C', 
                          fontWeight: '800', 
                          fontSize: '0.9rem'
                        }}>
                          {game.won ? `+${formatNumber(game.winnings)}` : 'LOSS'}
                        </div>
                      </div>
                      <div style={{ color: 'rgba(220, 20, 60, 0.6)', fontSize: '0.7rem' }}>
                        P: {game.playerTotal} | H: {game.houseTotal}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CryptoBlackjack;